import React from 'react';
import { Loader2, RefreshCw, Eye, Heart, MessageCircle, Share2, ExternalLink } from 'lucide-react';

export function TikTokIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M16.5 3c.3 2.1 1.6 3.8 3.7 4.1v2.6c-1.4.1-2.7-.3-3.8-1v6.3c0 3.3-2.7 6-6 6s-6-2.7-6-6 2.7-6 6-6c.3 0 .7 0 1 .1v2.7c-.3-.1-.7-.2-1-.2-1.8 0-3.3 1.5-3.3 3.3s1.5 3.3 3.3 3.3 3.3-1.5 3.3-3.3V3h2.8z" />
    </svg>
  );
}

export default function TikTokFeedbackView({ data, loading, onRefresh }) {
  if (loading && !data) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!data?.connected) {
    return (
      <div className="text-center py-20 border border-white/5 bg-[#111115]">
        <TikTokIcon className="w-10 h-10 text-white/10 mx-auto mb-3" />
        <p className="text-white/30 text-sm">TikTok isn't connected.</p>
        <p className="text-white/20 text-xs mt-1">Connect your TikTok account to see views, likes &amp; comments on your published videos.</p>
        {data?.error && (
          <p className="text-red-400/70 text-[11px] mt-2 font-mono">{data.error}</p>
        )}
      </div>
    );
  }

  const videos = data.videos || [];
  if (!videos.length) {
    return (
      <div className="text-center py-20 border border-white/5 bg-[#111115]">
        <TikTokIcon className="w-10 h-10 text-white/10 mx-auto mb-3" />
        <p className="text-white/30 text-sm">No public videos found on your TikTok account.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-white/40 text-xs">
          Feedback for your {videos.length} most recent TikTok {videos.length === 1 ? 'video' : 'videos'}.
        </p>
        <button
          onClick={onRefresh}
          className="flex items-center gap-1.5 text-xs font-bold text-white/40 hover:text-white border border-white/10 hover:border-white/30 px-3 py-2 transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" /> REFRESH
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {videos.map((v) => (
          <a
            key={v.id}
            href={v.share_url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="block border border-white/5 bg-[#111115] overflow-hidden hover:border-primary/30 transition-colors group"
          >
            <div className="relative aspect-[9/16] bg-[#0d0d0f]">
              {v.cover_url ? (
                <img src={v.cover_url} alt={v.title || 'TikTok video'} className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <TikTokIcon className="w-10 h-10 text-white/10" />
                </div>
              )}
              <div className="absolute top-2 right-2 bg-black/60 text-white/70 p-1 rounded-sm">
                <ExternalLink className="w-3 h-3" />
              </div>
            </div>
            <div className="p-3">
              <p className="text-white/80 text-xs line-clamp-2 min-h-[2.5rem]">{v.title || 'Untitled'}</p>
              <p className="text-white/30 text-[10px] mt-1 mb-2">
                {v.create_time ? new Date(v.create_time * 1000).toLocaleDateString('en-GB') : ''}
              </p>
              <div className="grid grid-cols-2 gap-1.5 text-xs">
                <span className="flex items-center gap-1 text-white/50"><Eye className="w-3 h-3" /> {v.views?.toLocaleString() ?? 0}</span>
                <span className="flex items-center gap-1 text-rose-400"><Heart className="w-3 h-3" /> {v.likes?.toLocaleString() ?? 0}</span>
                <span className="flex items-center gap-1 text-sky-400"><MessageCircle className="w-3 h-3" /> {v.comments?.toLocaleString() ?? 0}</span>
                <span className="flex items-center gap-1 text-emerald-400"><Share2 className="w-3 h-3" /> {v.shares?.toLocaleString() ?? 0}</span>
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}